import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Grid,
    Container,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Chip,
    Stack,
    Divider,
    TextField,
    InputAdornment,
    Tooltip,
    IconButton,
    Fade,
    Slide
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import { styled } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ColorData from 'Components/CstmTheme/ColorData';
import MobileUserCard from './MobileUserCard';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminApi from 'Service/Admin.api';
import { useDispatch } from 'react-redux';
import { setAlertData, startLoading, stopLoading } from '../../Redux/Screen/Action';
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from './ProfessionalUserList';

const CorporateList = () => {
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [users, setUsers] = useState([]);
    const theme = useTheme();
    const navigate = useNavigate();

    // Enhanced responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const [searchQuery, setSearchQuery] = useState("");
    const dispatch = useDispatch();

    useEffect(() => {
        if (searchQuery === "") fetchListData();
        else {
            const filteredUsers = users.filter((user: any) =>
                `${user.firstName} ${user.lastName}`
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
            );
            setUsers(filteredUsers);
        }
    }, [searchQuery]);

    useEffect(() => {
        fetchListData();
    }, []);

    const fetchListData = async () => {
        try {
            dispatch(
                startLoading({
                    loading: true,
                    loadtext: "Fetching Corporate User Data...",
                })
            );
            const response = await AdminApi.CorporateList({
                userType: "CORPORATEUSER",
            });
            if (response?.success && Array.isArray(response?.data)) {
                setUsers(response.data);
            } else {
                setUsers([]); // Ensure it's an array even if no data
            }
            dispatch(stopLoading());
        } catch (error) {
            dispatch(stopLoading());
        }
    };
    const handleSelectUser = (userId: number) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleVerifySelected = () => {
        setUsers((prev: any) =>
            prev.map((user: any) =>
                selectedUsers.includes(user.id) ? { ...user, isVerified: true } : user
            )
        );
        setSelectedUsers([]);
    };
    const handleVerifyUser = async (user: any) => {
        const { email } = user;
        try {
            const response = await AdminApi.VerifyCorporate({
                email: email,
                userType: "CORPORATEUSER",
            });
            if (response?.success) {
                dispatch(
                    setAlertData({
                        alertMessage: "User Verified Successfully",
                        alertVariant: "success",
                        openAlert: true,
                    })
                );
            }
            await fetchListData();
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ width: '100%', overflow: "auto", maxHeight: "90vh", pb: 10 }}>
                    {/* Back Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>

                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={isMobile ? 'column' : 'row'}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <VerifiedIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            component="h1"
                                            fontWeight="bold"
                                            color={ColorData.text.secondary}
                                            gutterBottom
                                        >
                                            Corporate User Management
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color="white"
                                        >
                                            Verify and manage corporate user accounts with ease
                                        </Typography>
                                    </Box>
                                </Box>

                                <Tooltip title="Refresh Data">
                                    <IconButton
                                        onClick={fetchListData}
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            color: 'inherit',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.3)',
                                                transform: 'rotate(180deg)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    {/* Search and Actions */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <SearchCard elevation={2}>
                                <CardContent sx={{ p: '16px !important' }}>
                                    <TextField
                                        fullWidth
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search corporate users..."
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                </CardContent>
                            </SearchCard>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" gap={2} height="100%" alignItems="center">
                                <Chip
                                    icon={<PersonIcon />}
                                    label={`Total Users: ${users.length}`}
                                    color="info"
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                />
                                {selectedUsers.length > 0 && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleVerifySelected}
                                        startIcon={<VerifiedIcon />}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Verify ({selectedUsers.length})
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Content Section */}
                    <Slide direction="up" in timeout={600}>
                        <Box>
                            {isMobile ? (
                                <Box>
                                    {users?.map((user: any, index) => (
                                        <MobileUserCard
                                            key={user?.id}
                                            user={user}
                                            index={index}
                                            selectedUsers={selectedUsers}
                                            handleSelectUser={handleSelectUser}
                                            handleVerifyUser={handleVerifyUser}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                    <TableContainer sx={{ overflow: 'auto' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell>S.No</StyledTableCell>
                                                    <StyledTableCell>First Name</StyledTableCell>
                                                    <StyledTableCell>Last Name</StyledTableCell>
                                                    <StyledTableCell>Email</StyledTableCell>
                                                    <StyledTableCell>Mobile Number</StyledTableCell>
                                                    <StyledTableCell>Org Name</StyledTableCell>
                                                    <StyledTableCell>Status</StyledTableCell>
                                                    <StyledTableCell align="center">Action</StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {users.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                                            <Typography variant="body1">No Corporate Users Found.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    users?.map((user: any, index) => (
                                                        <StyledTableRow key={user.id}>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {index + 1}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    {user.firstName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    {user.lastName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>{user.email}</TableCell>
                                                            <TableCell>{user.phoneNumber}</TableCell>
                                                            <TableCell>{user.corporateOrgName}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={user.isVerified ? 'Verified' : 'Pending'}
                                                                    color={user.isVerified ? 'success' : 'warning'}
                                                                    size="small"
                                                                    icon={user.isVerified ? <VerifiedIcon /> : undefined}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Button
                                                                    variant={user.isVerified ? "outlined" : "contained"}
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => handleVerifyUser(user)}
                                                                    disabled={user.isVerified}
                                                                    startIcon={<VerifiedIcon />}
                                                                    sx={{ borderRadius: 2 }}
                                                                >
                                                                    {user.isVerified ? 'Verified' : 'Verify'}
                                                                </Button>
                                                            </TableCell>
                                                        </StyledTableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            )}
                        </Box>
                    </Slide>
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default CorporateList;
